import { dgcToMist } from '@cpc/tx';
import { qtyLotsToTokenMist } from '~/config/clobConfig';

/** SVG / 图表用色 — 与 Explorer design token 一致 */
export const TRADE = {
	bg: '#ffffff',
	panel: '#F3F6F8',
	border: '#E3E6E8',
	text: '#566873',
	muted: '#758F9E',
	up: '#008C65',
	down: '#EB5A29',
	accent: '#1F6493',
	grid: '#E3E6E8',
	/** 订单簿中间价条 */
	midStripAccent: '#38BDF8',
	midStripGlow: 'rgba(56,189,248,0.28)',
} as const;

/** 中间价条与上下盘口的垂直间距 */
export const tradeMidStripWrapCls = 'my-3 shrink-0';

/** 订单簿中间价行：基础壳层（方向色由 tone 类叠加） */
export const tradeMidStripShellCls = 'shrink-0 rounded-md bg-[#0b1220]/90';

export type TradeMidStripTone = 'up' | 'down' | 'neutral';

/** 中间价条方向配色 */
export const tradeMidStripToneCls: Record<
	TradeMidStripTone,
	{ shell: string; wash: string; bar: string; price: string; badge: string; label: string; meta: string }
> = {
	up: {
		shell: 'border border-[#0ECB81]/50 shadow-[0_0_18px_rgba(14,203,129,0.22),inset_0_1px_0_rgba(46,229,157,0.18)]',
		wash: 'from-[#0ECB81]/28 via-[#0ECB81]/12 to-[#061510]/60',
		bar: 'bg-[#0ECB81] shadow-[0_0_10px_rgba(14,203,129,0.75)]',
		price: 'text-[#2EE59D] drop-shadow-[0_0_8px_rgba(14,203,129,0.55)]',
		badge: 'bg-[#0ECB81]/25 text-[#2EE59D] ring-1 ring-[#0ECB81]/40',
		label: 'text-[#0ECB81]/85',
		meta: 'text-[#6EE7B7]/90',
	},
	down: {
		shell: 'border border-[#F6465D]/50 shadow-[0_0_18px_rgba(246,70,93,0.22),inset_0_1px_0_rgba(255,107,129,0.18)]',
		wash: 'from-[#F6465D]/28 via-[#F6465D]/12 to-[#1a080c]/60',
		bar: 'bg-[#F6465D] shadow-[0_0_10px_rgba(246,70,93,0.75)]',
		price: 'text-[#FF6B81] drop-shadow-[0_0_8px_rgba(246,70,93,0.55)]',
		badge: 'bg-[#F6465D]/25 text-[#FF6B81] ring-1 ring-[#F6465D]/40',
		label: 'text-[#F6465D]/85',
		meta: 'text-[#FCA5A5]/90',
	},
	neutral: {
		shell: 'border border-[#38BDF8]/45 shadow-[0_0_16px_rgba(56,189,248,0.18),inset_0_1px_0_rgba(125,211,252,0.15)]',
		wash: 'from-[#38BDF8]/22 via-[#38BDF8]/10 to-[#071018]/60',
		bar: 'bg-[#38BDF8] shadow-[0_0_10px_rgba(56,189,248,0.65)]',
		price: 'text-[#7DD3FC] drop-shadow-[0_0_8px_rgba(56,189,248,0.45)]',
		badge: 'bg-[#38BDF8]/20 text-[#7DD3FC] ring-1 ring-[#38BDF8]/35',
		label: 'text-[#38BDF8]/80',
		meta: 'text-[#BAE6FD]/85',
	},
};

export function tradeFillSideCls(side: TradeDisplaySide): string {
	if (side === 'buy') return 'text-[#0ECB81] font-semibold';
	if (side === 'sell') return 'text-[#F6465D] font-semibold';
	return 'text-[#848E9C]';
}

/** 成交着色方向：优先 Hub taker_side，否则相对上一笔（更旧）价格 tick 推断 */
export type TradeDisplaySide = 'buy' | 'sell' | 'unknown';

export function resolveTradeDisplaySide(
	row: { tradeSide?: string | null; dataPerDgc?: string; dgcPerData: string },
	olderRow?: { dataPerDgc?: string; dgcPerData: string } | null,
): TradeDisplaySide {
	const side = row.tradeSide;
	if (side === 'buy' || side === 'sell') return side;
	if (!olderRow) return 'unknown';
	const p = Number.parseFloat(row.dataPerDgc ?? row.dgcPerData);
	const older = Number.parseFloat(olderRow.dataPerDgc ?? olderRow.dgcPerData);
	if (!Number.isFinite(p) || !Number.isFinite(older)) return 'unknown';
	if (p > older) return 'buy';
	if (p < older) return 'sell';
	return 'unknown';
}

export const tradeCls = {
	up: 'text-success-dark',
	down: 'text-issue-dark',
	text: 'text-steel-darker',
	textMuted: 'text-steel-dark',
	textDim: 'text-steel',
	link: 'text-sui-dark hover:underline',
	rowHover: 'hover:bg-gray-40',
	cosure: 'hover:bg-white/10',
	btnBuy: 'rounded-lg bg-success-dark px-4 py-2.5 text-bodySmall font-semibold text-white hover:bg-success disabled:cursor-not-allowed disabled:opacity-50',
	btnSell: 'rounded-lg bg-issue-dark px-4 py-2.5 text-bodySmall font-semibold text-white hover:bg-issue disabled:cursor-not-allowed disabled:opacity-50',
	input: 'flex h-10 items-stretch overflow-hidden rounded-lg border border-gray-45 bg-white focus-within:border-sui-dark',
	inputInner: 'min-w-0 flex-1 bg-transparent px-3 text-right text-bodySmall tabular-nums text-steel-darker outline-none',
} as const;

export type KlineInterval = '1m' | '5m' | '15m' | '1h';

export const KLINE_INTERVALS: KlineInterval[] = ['1m', '5m', '15m', '1h'];

/** Push Hub 订阅深度上限（买卖各 N 档） */
export const DEPTH_MAX_LEVELS = 100;
/** 盘口固定可见行数，超出部分滚动查看 */
export const DEPTH_VISIBLE_ROWS = 12;
export const DEPTH_ROW_PX = 25;

/** K 线 SVG 绘图区高度 */
export const KLINE_CHART_HEIGHT_PX = 280;

export function depthPaneHeightPx(rows = DEPTH_VISIBLE_ROWS): number {
	return rows * DEPTH_ROW_PX;
}

/** 深色面板滚动条（订单簿 / 成交列表复用） */
export const tradeScrollCls =
	'overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgba(56,189,248,0.45)_rgba(15,23,42,0.55)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-white/[0.06] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#38BDF8]/40 hover:[&::-webkit-scrollbar-thumb]:bg-[#38BDF8]/65';

/** 委托限价 / 最新价展示精度（quote per 1 base） */
export const TRADE_PRICE_DECIMALS = 6;

/** 顶部 ticker 余额展示精度 */
export const BALANCE_DISPLAY_DECIMALS = 6;

/** 余额展示：最多 6 位小数，去掉尾零 */
export function formatBalanceDisplay(
	raw: string | number | null | undefined,
	decimals = BALANCE_DISPLAY_DECIMALS,
): string {
	if (raw == null || raw === '') return '0';
	const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw).trim());
	if (!Number.isFinite(n)) return '0';
	const fixed = n.toFixed(decimals);
	return fixed.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '') || '0';
}

export function formatPrice(v: number): string {
	if (!Number.isFinite(v) || v === 0) return '—';
	return formatLastTradePrice(String(v));
}

/** 最新成交价（最多 6 位小数，与交易所 Last 一致） */
export function formatLastTradePrice(
	raw: string | number | null | undefined,
	fallback = '—',
): string {
	if (raw == null || raw === '') return fallback;
	const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw).trim());
	if (!Number.isFinite(n) || n <= 0) return fallback;
	const fixed = n.toFixed(TRADE_PRICE_DECIMALS);
	return fixed.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '') || '0';
}

/** 成交列表价格：固定 6 位小数（不省略尾零） */
export function formatTradePriceFixed(
	raw: string | number | null | undefined,
	fallback = '—',
): string {
	if (raw == null || raw === '') return fallback;
	const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw).trim());
	if (!Number.isFinite(n) || n <= 0) return fallback;
	return n.toFixed(TRADE_PRICE_DECIMALS);
}

/** 最新成交固定显示行数 */
export const RECENT_TRADE_VISIBLE_ROWS = 27;

export function recentTradesPaneHeightPx(
	rows = RECENT_TRADE_VISIBLE_ROWS,
): number {
	return rows * DEPTH_ROW_PX;
}

/** 限价输入：数字 + 至多 maxDecimals 位小数（iOS 小数键盘 / 逗号） */
export function sanitizeLimitPriceInput(
	raw: string,
	maxDecimals = TRADE_PRICE_DECIMALS,
): string {
	let s = raw.replace(/[,，。．]/g, '.').replace(/[^\d.]/g, '');
	if (!s) return '';
	const dot = s.indexOf('.');
	if (dot >= 0) {
		const intPart = s.slice(0, dot) || '0';
		const decPart = s.slice(dot + 1).replace(/\./g, '').slice(0, maxDecimals);
		s = decPart.length > 0 ? `${intPart}.${decPart}` : intPart + (raw.includes('.') ? '.' : '');
	}
	if (s.startsWith('.')) s = `0${s}`;
	return s;
}

/** 数量：与限价同一套小数（最多 9 位，对齐 coin decimals）。 */
export function sanitizeQtyInput(raw: string, maxDecimals = 9): string {
	return sanitizeLimitPriceInput(raw, maxDecimals);
}

export const DEFAULT_CLOB_LOT_SIZE_MIST = 1_000_000_000n;

/** 人类可读枚数 → qty_lots（= base mist / lot_size）。不足一手则抛 not_lot。 */
export function parseQtyLots(
	raw: string,
	lotSizeMist: bigint = DEFAULT_CLOB_LOT_SIZE_MIST,
): bigint {
	const s = sanitizeQtyInput(raw.trim());
	if (!s || s === '.') throw new Error('empty');
	const normalized = s.endsWith('.') ? s.slice(0, -1) : s;
	if (!normalized) throw new Error('empty');
	let mist: bigint;
	try {
		mist = dgcToMist(normalized);
	} catch {
		throw new Error('invalid');
	}
	if (mist <= 0n) throw new Error('not_positive');
	if (lotSizeMist <= 0n) throw new Error('bad_lot');
	if (mist % lotSizeMist !== 0n) throw new Error('not_lot');
	return mist / lotSizeMist;
}

export function tryParseQtyLots(
	raw: string,
	lotSizeMist: bigint = DEFAULT_CLOB_LOT_SIZE_MIST,
): bigint | null {
	try {
		return parseQtyLots(raw, lotSizeMist);
	} catch {
		return null;
	}
}

export function formatTimeMs(ms: number): string {
	const d = new Date(ms);
	return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

export function formatTimeFull(ms: number | null | undefined): string {
	return formatTradeTime(ms);
}

/** 成交列表时间列：窄栏 HH:mm:ss，跨日 MM-DD */
export function formatTradeTimeColumn(ms: number | null | undefined): string {
	if (ms == null) return '—';
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return '—';
	const pad = (n: number) => n.toString().padStart(2, '0');
	const clock = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
	if (d.toDateString() === new Date().toDateString()) return clock;
	return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 成交列表时间：当天 HH:mm:ss，跨日 MM-DD HH:mm:ss */
export function formatTradeTime(ms: number | null | undefined): string {
	if (ms == null) return '—';
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return '—';
	const pad = (n: number) => n.toString().padStart(2, '0');
	const clock = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
	if (d.toDateString() === new Date().toDateString()) return clock;
	return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${clock}`;
}

export function pctChange(first: number, last: number): number | null {
	if (!first || !Number.isFinite(first)) return null;
	return ((last - first) / first) * 100;
}

/** 24h 行情统计（由成交列表滚动计算） */
export type Ticker24h = {
	changePct: number | null;
	open: number | null;
	high: number | null;
	low: number | null;
	volumeLots: bigint;
	turnoverCpc: number;
	tradeCount: number;
};

const MS_24H = 86_400_000;

export function computeTicker24h(
	trades: {
		timestampMs?: number | null;
		dataPerDgc?: string;
		dgcPerData: string;
		qtyLots?: string;
	}[],
	fallbackPrice: number,
	_lotSizeMist: bigint = DEFAULT_CLOB_LOT_SIZE_MIST,
): Ticker24h {
	const cutoff = Date.now() - MS_24H;
	const recent = trades.filter((t) => (t.timestampMs ?? 0) >= cutoff);
	if (recent.length === 0) {
		return {
			changePct: null,
			open: fallbackPrice || null,
			high: fallbackPrice || null,
			low: fallbackPrice || null,
			volumeLots: 0n,
			turnoverCpc: 0,
			tradeCount: 0,
		};
	}
	let high = 0;
	let low = Infinity;
	let vol = 0n;
	let turnover = 0;
	const prices: number[] = [];
	for (const t of recent) {
		const p = Number.parseFloat(t.dataPerDgc ?? t.dgcPerData);
		if (Number.isFinite(p)) {
			prices.push(p);
			high = Math.max(high, p);
			low = Math.min(low, p);
			const tokenMist = qtyLotsToTokenMist(t.qtyLots, t.timestampMs);
			turnover += p * (Number(tokenMist) / 1e9);
		}
		vol += qtyLotsToTokenMist(t.qtyLots, t.timestampMs) / 1_000_000_000n;
	}
	const open = prices[prices.length - 1] ?? fallbackPrice;
	const last = prices[0] ?? fallbackPrice;
	return {
		changePct: pctChange(open, last),
		open: open || null,
		high: high || null,
		low: low === Infinity ? null : low,
		volumeLots: vol,
		turnoverCpc: turnover,
		tradeCount: recent.length,
	};
}

/** 盘口最优买卖价与价差 */
export type BookTicker = {
	bestBid: string | null;
	bestAsk: string | null;
	spread: number | null;
	spreadPct: number | null;
};

export function computeBookTicker(
	bids: { priceTicks: string; dataPerDgc?: string; dgcPerData: string }[],
	asks: { priceTicks: string; dataPerDgc?: string; dgcPerData: string }[],
): BookTicker {
	const bestBidRow = [...bids].sort((a, b) =>
		BigInt(b.priceTicks) > BigInt(a.priceTicks) ? 1 : -1,
	)[0];
	const bestAskRow = [...asks].sort((a, b) =>
		BigInt(a.priceTicks) > BigInt(b.priceTicks) ? 1 : -1,
	)[0];
	// 盘口比较用 DATA/DGC（与 price_ticks 同向）；兼容旧字段
	const bestBid = bestBidRow?.dataPerDgc ?? bestBidRow?.dgcPerData ?? null;
	const bestAsk = bestAskRow?.dataPerDgc ?? bestAskRow?.dgcPerData ?? null;
	const bidN = bestBid ? Number.parseFloat(bestBid) : NaN;
	const askN = bestAsk ? Number.parseFloat(bestAsk) : NaN;
	if (!Number.isFinite(bidN) || !Number.isFinite(askN) || askN <= bidN) {
		return { bestBid, bestAsk, spread: null, spreadPct: null };
	}
	const spread = askN - bidN;
	return {
		bestBid,
		bestAsk,
		spread,
		spreadPct: bidN > 0 ? (spread / bidN) * 100 : null,
	};
}

/** 大数量紧凑展示（K / M / B） */
export function formatCompactQty(raw: bigint | number): string {
	const n = typeof raw === 'bigint' ? Number(raw) : raw;
	if (!Number.isFinite(n) || n === 0) return '0';
	if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
	if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
	if (n >= 1e4) return `${(n / 1e3).toFixed(2)}K`;
	if (Number.isInteger(n)) return n.toLocaleString('en-US');
	return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
